import { useEventMixpanel } from '@mixpanel';
import useTranslation from 'next-translate/useTranslation';

import { DrawerLine, Typography } from '@core/component';
import { DrawerCloseProps, EDrawerLevel } from '@core/component/interface';
import { metalPriceToSlug, Translate } from '@core/constant';
import { useContextCurrency } from '@core/context';
import { useLanguage } from '@core/hook';
import { EHeaderMenu, EMetal, EMixpanelHeaderSectionType, ERouting } from '@core/type';
import { SanityHeaderDefaultResponse } from '@core/type/api';

import { SideNavDefaultProps } from '../../../../interface-header';
import { getHeaderLinkHref } from '../../header-menu/utils';
import { RecursiveDrawerLineProps } from './interface-side-nav';

const RecursiveDrawerLine = ({
  link,
  links,
  menuType,
  closeDrawer,
  drawerLevelProps,
  handleDrawerLevelProps,
  previousDrawerLevel,
  subMenus,
  iconType,
}: RecursiveDrawerLineProps) => {
  const { trackHeaderEvent } = useEventMixpanel();

  if (menuType === EHeaderMenu.FULLWIDTH_DROPDOWN) {
    return (
      <DrawerLine
        closeDrawer={closeDrawer}
        drawerLevel={`${previousDrawerLevel}/${link.text}` as EDrawerLevel}
        title={link.text}
        drawerLevelProps={drawerLevelProps}
        handleDrawerLevelProps={handleDrawerLevelProps}
        trackEvent={() =>
          trackHeaderEvent({
            sectionName: link.text,
            sectionType: EMixpanelHeaderSectionType.MENU,
          })
        }
      >
        {subMenus.map((menu) => (
          <RecursiveDrawerLine
            key={menu._key}
            {...menu}
            drawerLevelProps={drawerLevelProps}
            previousDrawerLevel={`${previousDrawerLevel}/${link.text}`}
            handleDrawerLevelProps={handleDrawerLevelProps}
            closeDrawer={closeDrawer}
          />
        ))}
      </DrawerLine>
    );
  }

  if (links) {
    return (
      <DrawerLine
        closeDrawer={closeDrawer}
        drawerLevel={`${previousDrawerLevel}/${link.text}` as EDrawerLevel}
        href={link.href}
        title={link.text}
        drawerLevelProps={drawerLevelProps}
        handleDrawerLevelProps={handleDrawerLevelProps}
        trackEvent={() =>
          trackHeaderEvent({
            sectionName: link.text,
            sectionType: previousDrawerLevel
              ? EMixpanelHeaderSectionType.SUB_MENU
              : EMixpanelHeaderSectionType.MENU,
            urlPath: getHeaderLinkHref(link),
          })
        }
      >
        {links.map((link) => (
          <DrawerLine
            key={link.id}
            closeDrawer={closeDrawer}
            drawerLevel={`${previousDrawerLevel}/${link.text}` as EDrawerLevel}
            href={getHeaderLinkHref(link)}
            title={<Typography>{link.text}</Typography>}
            drawerLevelProps={drawerLevelProps}
            handleDrawerLevelProps={closeDrawer}
            iconType={iconType}
            trackEvent={() =>
              trackHeaderEvent({
                sectionName: link.text,
                sectionType: EMixpanelHeaderSectionType.LINK,
              })
            }
          />
        ))}
      </DrawerLine>
    );
  }

  return (
    <DrawerLine
      closeDrawer={closeDrawer}
      drawerLevel={`${previousDrawerLevel}/${link.text}` as EDrawerLevel}
      href={getHeaderLinkHref(link)}
      title={link.text}
      drawerLevelProps={drawerLevelProps}
      handleDrawerLevelProps={closeDrawer}
      iconType={iconType}
      trackEvent={() =>
        trackHeaderEvent({
          sectionName: link.text,
          sectionType: EMixpanelHeaderSectionType.MENU,
          urlPath: getHeaderLinkHref(link),
        })
      }
    />
  );
};

const LiveChartsMenu = ({
  closeDrawer,
  drawerLevelProps,
  handleDrawerLevelProps,
}: DrawerCloseProps) => {
  const { currency } = useContextCurrency();
  const { language } = useLanguage();
  const { t } = useTranslation(Translate.layout.HEADER);

  return (
    <DrawerLine
      closeDrawer={closeDrawer}
      drawerLevel={EDrawerLevel.CHARTS}
      drawerLevelProps={drawerLevelProps}
      handleDrawerLevelProps={handleDrawerLevelProps}
      title={t('menu.liveCharts')}
    >
      <DrawerLine
        closeDrawer={closeDrawer}
        title={`${t('menu.gold')} ${t('menu.charts')}`}
        drawerLevelProps={drawerLevelProps}
        route={ERouting.CHARTS}
        query={{
          metalSlug: metalPriceToSlug[language][EMetal.GOLD],
          slug: [currency.toLowerCase()],
        }}
        handleDrawerLevelProps={closeDrawer}
      />
      <DrawerLine
        closeDrawer={closeDrawer}
        title={`${t('menu.silver')} ${t('menu.charts')}`}
        route={ERouting.CHARTS}
        query={{
          metalSlug: metalPriceToSlug[language][EMetal.SILVER],
          slug: [currency.toLowerCase()],
        }}
        drawerLevelProps={drawerLevelProps}
        handleDrawerLevelProps={closeDrawer}
      />
      <DrawerLine
        closeDrawer={closeDrawer}
        title={`${t('menu.platinium')} ${t('menu.charts')}`}
        drawerLevelProps={drawerLevelProps}
        route={ERouting.CHARTS}
        query={{
          metalSlug: metalPriceToSlug[language][EMetal.PLATINUM],
          slug: [currency.toLowerCase()],
        }}
        handleDrawerLevelProps={closeDrawer}
      />
      <DrawerLine
        closeDrawer={closeDrawer}
        title={`${t('menu.palladium')} ${t('menu.charts')}`}
        drawerLevelProps={drawerLevelProps}
        route={ERouting.CHARTS}
        query={{
          metalSlug: metalPriceToSlug[language][EMetal.PALLADIUM],
          slug: [currency.toLowerCase()],
        }}
        handleDrawerLevelProps={closeDrawer}
      />
    </DrawerLine>
  );
};

export const Menus = ({
  closeDrawer,
  drawerLevelProps,
  handleDrawerLevelProps,
  menus,
}: SideNavDefaultProps & SanityHeaderDefaultResponse) => {
  return (
    <>
      {menus?.map((menu) => (
        <RecursiveDrawerLine
          key={menu._key}
          {...menu}
          previousDrawerLevel=""
          drawerLevelProps={drawerLevelProps}
          closeDrawer={closeDrawer}
          handleDrawerLevelProps={handleDrawerLevelProps}
        />
      ))}
      <LiveChartsMenu
        closeDrawer={closeDrawer}
        drawerLevelProps={drawerLevelProps}
        handleDrawerLevelProps={handleDrawerLevelProps}
      />
    </>
  );
};
